import React, { useEffect, useMemo, useState } from "react";

const DocumentPreview = ({
  url,
  previewUrl,
  title = "Document Preview",
  className,
  style,
  height,
  width,
  showActions = true,
  showOpenInNewTab = true,
  onLoad,
  onError,
  fileSize,
}) => {
  const [viewerIndex, setViewerIndex] = useState(0);
  const [isFailed, setIsFailed] = useState(false);

  const displayUrl = previewUrl || url;

  useEffect(() => {
    setViewerIndex(0);
    setIsFailed(false);
  }, [displayUrl]);

  const sourceList = useMemo(() => {
    if (!displayUrl || typeof displayUrl !== "string") return [];

    const cleanUrl = displayUrl.split("?")[0].toLowerCase();
    const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(cleanUrl);
    const isPdf = /\.pdf$/.test(cleanUrl);
    const isOfficeDoc = /\.(doc|docx|xls|xlsx|ppt|pptx)$/.test(cleanUrl);

    const officeViewer = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(displayUrl)}`;
    const gViewer = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(displayUrl)}`;

    if (isImage) return [displayUrl];
    if (isPdf) return [displayUrl, gViewer];
    if (isOfficeDoc) return [officeViewer, gViewer, displayUrl];

    return [gViewer, displayUrl];
  }, [displayUrl]);

  if (!url || sourceList.length === 0) {
    return (
      <div style={{ width: width || "100%", height, padding: "20px", textAlign: "center", background: "#f8fafc", borderRadius: 6, border: "1px dashed #cbd5e1" }}>
        <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>Invalid document URL</p>
      </div>
    );
  }

  const previewStyle = {
    ...(style || {}),
    ...(height ? { height } : {}),
    ...(width ? { width } : {}),
  };

  const handleLoadError = () => {
    if (viewerIndex < sourceList.length - 1) {
      setViewerIndex((prev) => prev + 1);
      return;
    }

    setIsFailed(true);
    if (onError) onError();
  };

  const renderFallback = () => (
    <div
      className="doc-preview-fallback"
      style={{
        ...previewStyle,
        width: width || "100%",
        minHeight: height || 120,
        border: "1px dashed #cbd5e1",
        borderRadius: 8,
        background: "#f1f5f9",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: 12,
        position: "relative",
      }}
    >
      <p style={{ margin: 0, fontSize: 14, color: "#64748b", fontWeight: 500 }}>Preview unavailable</p>
      {fileSize && <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>Size: {fileSize}</p>}
      {renderActions(true)}
    </div>
  );

  const renderActions = (isFallback = false) => {
    if (!showActions) return null;

    return (
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "nowrap",
          padding: "8px 12px",
          background: isFallback ? "transparent" : "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
          gap: 12,
          boxSizing: "border-box",
          zIndex: 10,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        {showOpenInNewTab && (
          <a
            href={displayUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              flex: 1,
              textAlign: "center",
              textDecoration: "none",
              background: "#3b82f6",
              color: "#ffffff",
              padding: "8px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: "600",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              transition: "background 0.2s",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
            onMouseOver={(e) => e.target.style.background = "#2563eb"}
            onMouseOut={(e) => e.target.style.background = "#3b82f6"}
          >
            Preview ↗
          </a>
        )}
        <a
          href={url}
          download
          style={{
            flex: 1,
            textAlign: "center",
            textDecoration: "none",
            background: "#10b981",
            color: "#ffffff",
            padding: "8px",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "600",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            transition: "background 0.2s",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
          onMouseOver={(e) => e.target.style.background = "#059669"}
          onMouseOut={(e) => e.target.style.background = "#10b981"}
        >
          Download ↓
        </a>
      </div>
    );
  };

  const cleanUrl = displayUrl.split("?")[0].toLowerCase();
  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(cleanUrl);

  if (isFailed) {
    return renderFallback();
  }

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: width || "100%",
        height: height || "auto",
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        ...style
      }}
    >
      {isImage ? (
        <img
          src={sourceList[viewerIndex]}
          alt={title}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            ...(height ? { height } : {})
          }}
          onLoad={onLoad}
          onError={handleLoadError}
        />
      ) : (
        <iframe
          src={sourceList[viewerIndex]}
          title={title}
          style={{
            width: "100%",
            height: height || 300,
            border: "none",
            display: "block"
          }}
          onLoad={onLoad}
          onError={handleLoadError}
        />
      )}
      {renderActions()}
    </div>
  );
};

export default DocumentPreview;
